import React from 'react';
import { Award, Users, Target, Shield, Handshake, TrendingUp, BookOpen, Sparkles, Heart } from 'lucide-react';

const Embaixadores = () => {
    const selectionCriteria = [
        "Graduado da UEM",
        "Capacidade de intervenção na vida da Faculdade/Escola",
        "Mérito académico e profissional reconhecidos pelos pares",
        "Representatividade geracional"
    ];

    const missions = [
        {
            icon: <Handshake className="w-5 h-5" />,
            title: "Angariação de Fundos",
            description: "Mobilizar a angariação de fundos para apoiar iniciativas institucionais."
        },
        {
            icon: <BookOpen className="w-5 h-5" />,
            title: "Apoio Académico",
            description: "Mobilizar apoios para o bom decurso das actividades de ensino-aprendizagem e investigações dos estudantes da UEM."
        },
        {
            icon: <TrendingUp className="w-5 h-5" />,
            title: "Parcerias Estratégicas",
            description: "Identificar e/ou promover parcerias e colaborar em actividades que visem potenciar o crescimento da UEM."
        }
    ];

    const supportTypes = [
        "Estágios em instituições públicas ou privadas",
        "Bibliografia e material didático",
        "Equipamento de laboratórios",
        "Material informático",
        "Bolsas de estudo"
    ];

    return (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
            {/* Header Section */}
            <div className="mb-12">
                <div className="border-l-4 border-emerald-500 pl-4 mb-4">
                    <h1 className="text-3xl font-light text-gray-800 tracking-tight">Embaixadores</h1>
                    <p className="text-gray-500 text-base font-light mt-1">
                        Representantes da UEM no país e no mundo
                    </p>
                </div>
            </div>

            {/* Introduction */}
            <div className="mb-8">
                <p className="text-gray-600 leading-relaxed">
                    Aquando do lançamento da Iniciativa, as Faculdades e Escolas da UEM procederam à selecção
                    dos seus Embaixadores. À altura do lançamento já haviam sido seleccionados
                    <span className="font-medium text-emerald-600"> cinquenta e três (53) embaixadores</span>,
                    conjugando os seguintes critérios:
                </p>
            </div>

            {/* Selection Criteria */}
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100 mb-8">
                <div className="flex items-center gap-2 mb-4">
                    <Award className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Critérios de Seleção</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectionCriteria.map((criterion, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                            <span className="text-gray-600 text-sm">{criterion}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Ambassador Role */}
            <div className="bg-gradient-to-r from-emerald-50/30 to-gray-50/30 rounded-xl p-6 border border-emerald-100/30 mb-8">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h2 className="text-base font-medium text-gray-700">Papel do Embaixador</h2>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                    O(a) embaixador(a) Alumni-UEM representa a UEM no país e no mundo através da defesa
                    da visão e missão da instituição e da protecção dos seus símbolos e insígnias que
                    fazem da UEM, uma marca de vanguarda no ensino superior no país, na região e
                    internacionalmente.
                </p>
            </div>

            {/* Mission Section */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-5">
                    <Target className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Missão do Embaixador</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {missions.map((mission, index) => (
                        <div key={index} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all duration-300 group">
                            <div className="flex justify-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                                    <div className="text-emerald-500">
                                        {mission.icon}
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-sm font-medium text-gray-700 text-center mb-2">
                                {mission.title}
                            </h3>
                            <p className="text-gray-500 text-xs text-center leading-relaxed">
                                {mission.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Support Types */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Tipos de Apoio</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                    {supportTypes.map((type, index) => (
                        <span 
                            key={index}
                            className="px-3 py-1.5 bg-gray-50 text-gray-600 text-xs rounded-full border border-gray-100"
                        >
                            {type}
                        </span>
                    ))}
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-5">
                <div className="flex justify-around items-center">
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">53</div>
                        <div className="text-xs text-emerald-100">Embaixadores</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">10+</div>
                        <div className="text-xs text-emerald-100">Faculdades</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">Nacional</div>
                        <div className="text-xs text-emerald-100">Representação</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Embaixadores;